import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client.js";
import { LODGING, PUBLIC_RESOURCES, SEPAQ_CHALET_URL, STAY_RESOURCE_IDS } from "@/content/expedition.js";
import { OfficialLogo } from "@/components/OfficialLogo.jsx";
import { PhotoLightbox } from "@/components/PhotoLightbox.jsx";
import { TerritoryMap } from "@/components/TerritoryMap.jsx";
import { useOrignalUi } from "@/hooks/useOrignalUi.js";

function dayTitle(iso) {
  if (iso === "2026-08-28") return "Vendredi 28 août";
  if (iso === "2026-08-29") return "Samedi 29 août";
  if (iso === "2026-08-30") return "Dimanche 30 août";
  return iso || "";
}

export function ProspectionPage() {
  const { ui } = useOrignalUi();
  const t = ui.hunt;
  const [story, setStory] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [limit, setLimit] = useState(null);
  const [bilan, setBilan] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [index, setIndex] = useState(-1);

  useEffect(() => {
    api("/api/prospection/story").then(setStory).catch(() => {});
    api("/api/prospection/map").then(setMapData).catch(() => {});
    api("/api/territory/secteur4").then(setLimit).catch(() => {});
    api("/api/bootstrap").then((d) => setBilan(d.hunt?.prospection?.bilan)).catch(() => {});
  }, []);

  const weekend = story?.weekend_moments || [];
  const byDay = {};
  for (const m of weekend) {
    const d = m.day || "autre";
    (byDay[d] ||= []).push(m);
  }
  const days = ["2026-08-28", "2026-08-29", "2026-08-30"].filter((d) => byDay[d]?.length);
  const gang = story?.present || [];
  const mapPoints = [
    ...(mapData?.points || []).map((p) => ({ ...p, marker: "terrain" })),
    ...(mapData?.photos_gps || []).flatMap((c) =>
      (c.photos || []).map((ph) => ({
        id: ph.id,
        latitude: ph.gps_latitude,
        longitude: ph.gps_longitude,
        name: ph.fichier,
        category: "photo_gps",
        marker: "photo",
        url: ph.url,
        date_heure: ph.date_heure,
      })),
    ),
  ];

  return (
    <main className="og-home og-home--chapter og-storybook" data-testid="page-trip">
      <section className="og-panel" data-testid="chalet-card">
        <div className="og-identity">
          <OfficialLogo size="page" decorative />
        </div>
        <h1 data-testid="prospect-heading">{t.prospectTitle}</h1>
        <p data-testid="prospect-dates">{story?.dates || t.prospectDates}</p>
        <p>{LODGING.name} · {ui.expedition.sectorBadge}</p>
        <p data-testid="compact-itinerary"><strong>{ui.expedition.compactLine}</strong></p>
        <p><span className="og-badge" data-testid="prospection-status">{t.completed}</span></p>
        <a className="og-btn" href={SEPAQ_CHALET_URL} target="_blank" rel="noreferrer" data-testid="sepaq-link">{ui.expedition.seeSepaq}</a>
      </section>

      <section className="og-panel" data-testid="gang-present">
        <h2>{t.gangPresent}</h2>
        <p data-testid="prospect-count">{t.sevenPeople}</p>
        <ul className="og-home__couples">
          {gang.map((p) => (
            <li key={p.id}>{p.first_name || p.display_name}</li>
          ))}
        </ul>
        <p className="og-hint">{t.pierreRobinOut}</p>
        <p className="og-hint">{story?.josee_note || t.joseeAway}</p>
      </section>

      <section className="og-panel" data-testid="voyage-timeline" data-historical="prospect">
        <h2>{t.ourStory}</h2>
        <p className="og-hint">{t.storyLead}</p>
        {days.length ? days.map((day) => (
          <div key={day} className="og-story-day">
            <h3>{dayTitle(day)}</h3>
            {byDay[day].map((mom) => {
              const photos = (mom.photos || []).filter((p) => p.url);
              return (
                <article key={mom.id} className="og-story-moment" data-testid={`moment-${mom.id}`}>
                  <h4>{mom.confirmation_required ? mom.label_neutral : mom.title}</h4>
                  {mom.confirmation_required && mom.title_suggested ? (
                    <p className="og-hint">{t.suggested}: {mom.title_suggested}</p>
                  ) : null}
                  {photos.length ? (
                    <div className="og-album og-album--moment">
                      {photos.map((p, i) => (
                        <button
                          key={p.id}
                          type="button"
                          className="og-album__shot"
                          style={{ backgroundImage: `url(${p.url})` }}
                          onClick={() => { setGallery(photos); setIndex(i); }}
                        >
                          <span>{p.location_note || ""}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {(mom.messages || []).map((m) => (
                    <p key={m.id}><strong>{m.author}</strong> — {m.body}</p>
                  ))}
                </article>
              );
            })}
          </div>
        )) : <p className="og-hint">{ui.app.loading}</p>}
      </section>

      <section className="og-panel" data-testid="prospect-map-block">
        <h2>{t.mapWeekend}</h2>
        <p className="og-hint">{mapData?.note || t.noPhotoGps}</p>
        <TerritoryMap points={mapPoints} limit={limit} full={false} testId="weekend-map" />
      </section>

      <section className="og-panel" id="photos">
        <h2>{t.allPhotos}</h2>
        <Link className="og-btn og-btn--primary" to="/photos">{t.seePhotos}</Link>
      </section>

      <section className="og-panel" data-testid="prospect-bilan-block">
        <h2>{t.weekendCost}</h2>
        <p>{bilan?.label || t.bilanPending}</p>
        {bilan?.per_couple ? <p>{bilan.per_couple} {t.perCouple}</p> : null}
        <Link className="og-btn" to="/depenses">{t.seeExpenses}</Link>
      </section>

      <p className="og-hint" data-historical="prospect" data-testid="prospect-tool-history">{t.toolHistory}</p>
      <section className="og-panel" data-testid="trip-resources">
        {PUBLIC_RESOURCES.filter((r) => STAY_RESOURCE_IDS.includes(r.id)).map((r) => (
          <a key={r.id} className="og-resource" href={r.url} target="_blank" rel="noreferrer">{r.title}</a>
        ))}
      </section>

      {index >= 0 ? (
        <PhotoLightbox items={gallery} index={index} onIndex={setIndex} onClose={() => setIndex(-1)} ui={ui} />
      ) : null}
    </main>
  );
}
